import { useEffect, useRef } from "react";

interface ModalProps {
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  hasCloseButton?: boolean;
  headerContent?: React.ReactNode;
}

export type ModalSize = "small" | "medium" | "large" | "fullscreen";

export function Modal({
  children,
  onClose,
  size,
  title = "",
  hasCloseButton = true,
  headerContent,
}: ModalProps) {
  let originalOverflow = null;
  const contentRef = useRef<HTMLDivElement>(null);
  const mouseDownOnOverlayRef = useRef(false);

  useEffect(() => {
    // Prevent background scrolling
    document.body.classList.add("hide-scrollbar");

    return () => {
      document.body.classList.remove("hide-scrollbar");
    };
  }, []);

  const handleClose = () => {
    if (originalOverflow) {
      document.body.style.overflow = originalOverflow;
    }
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const active = document.activeElement;
      const content = contentRef.current;
      const focusInsideModal =
        !!active && !!content && content.contains(active);
      const isEditable =
        focusInsideModal &&
        (active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active instanceof HTMLSelectElement ||
          (active instanceof HTMLElement && active.isContentEditable));
      if (isEditable) return;
      handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseDownOnOverlayRef.current = e.target === e.currentTarget;
  };

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const startedOnOverlay = mouseDownOnOverlayRef.current;
    mouseDownOnOverlayRef.current = false;
    if (startedOnOverlay && e.target === e.currentTarget) {
      handleClose();
    }
  };

  let sizeClass = "";
  switch (size) {
    case "small":
      sizeClass = "modal-content--small";
      break;
    case "medium":
      sizeClass = "modal-content--medium";
      break;
    case "large":
      sizeClass = "modal-content--large";
      break;
    case "fullscreen":
      sizeClass = "modal-content--fullscreen";
      break;
    default:
      sizeClass = "";
      break;
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div
        ref={contentRef}
        className={"modal-content " + sizeClass}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          {headerContent && (
            <div className="modal-header-content">{headerContent}</div>
          )}
          {hasCloseButton && (
            <button
              className="modal-close"
              onClick={handleClose}
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
